/**
 * Everything about one movement, in a panel that opens from the row.
 *
 * ## Why the words on it are not written here
 *
 * Every label, every hint, every account name arrives from the API. This file lays them out
 * and formats money and dates, and that is all it decides. The point is that "what does the
 * admin call this" has one answer in one place: an operator reading a panel should never meet
 * a word the rest of the console does not use, and nothing enforces that when two clients each
 * phrase things for themselves.
 *
 * It also means a fact appearing or disappearing is a server change alone. A bank top-up shows
 * the account number the money landed in; a crypto deposit shows the chain and the blockchain
 * transaction. Neither kind needed a branch here, and a new kind will not need one either.
 *
 * ## Why the ledger legs are on the same screen
 *
 * They are what actually happened, as opposed to what the row's status claims happened. A
 * payout stuck on "pending" with a completed entry behind it is a status that failed to
 * advance; the same row with no entry at all is money that never moved. From the list those
 * two are identical, and telling them apart is most of what an operator opens this panel for.
 *
 * ## Why nothing here changes anything
 *
 * Read-only, deliberately. The tools on it copy a reference, open the customer, and search a
 * vendor's own dashboard - the three things somebody actually does next. Retrying or reversing
 * a movement is a decision with its own audit trail and its own confirmation, and burying one
 * behind a row click is how it gets pressed by accident.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import type {
  AdminTxCharge,
  AdminTxCustomer,
  AdminTxDetail,
  AdminTxFact,
  AdminTxPosting,
} from '../contracts/types.js';
import { AdminAvatar } from './AdminAvatar.js';
import { TxStatusBadge } from './badges.js';
import { ContactPhone } from './ContactPhone.js';
import { Modal } from './Modal.js';
import { Badge, Button, CopyButton, ErrorBox, LoadingState } from './ui.js';
import { humanize, formatDateTime, relativeTime } from '../lib/format.js';
import { formatMoney } from '../lib/money.js';

export function TransactionDetail({
  id,
  onClose,
}: {
  /** The row that was clicked, or null when the panel is closed. */
  readonly id: string | null;
  readonly onClose: () => void;
}): JSX.Element | null {
  const { api } = useAuth();
  const [detail, setDetail] = useState<AdminTxDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id === null) {
      // Cleared on close rather than left behind, so reopening a different row never shows
      // the previous one's figures for the instant before the new ones land. On a money
      // screen that flash is not cosmetic: it is somebody else's balance under this row's
      // heading, and it is read.
      setDetail(null);
      setError(null);
      return;
    }

    let live = true;
    setDetail(null);
    setError(null);
    api
      .transaction(id)
      .then((d) => {
        if (live) setDetail(d);
      })
      .catch((e: unknown) => {
        if (live) setError(messageOf(e));
      });
    return () => {
      live = false;
    };
  }, [api, id]);

  if (id === null) return null;

  return (
    <Modal
      open
      size="wide"
      title={detail === null ? 'Loading' : humanize(detail.row.kind)}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {error !== null ? (
        <ErrorBox message={error} />
      ) : detail === null ? (
        <LoadingState label="Opening" />
      ) : (
        <Body detail={detail} />
      )}
    </Modal>
  );
}

function Body({ detail }: { readonly detail: AdminTxDetail }): JSX.Element {
  const { row, customer, facts, postings, charges, timeline } = detail;

  return (
    <div className="tx-detail">
      <div className="tx-detail__hero">
        <div>
          <div className="tx-detail__amount">{formatMoney(row.amount)}</div>
          <div className="muted">
            {formatDateTime(row.createdAt)} · {relativeTime(row.createdAt)}
          </div>
        </div>
        <TxStatusBadge status={row.status} />
      </div>

      {customer !== null && <Customer customer={customer} />}

      <Section title="What happened">
        <dl className="tx-detail__facts">
          {facts.map((f) => (
            <Fact key={f.label} fact={f} />
          ))}
        </dl>
      </Section>

      {/*
        The double entry. Shown even when it is empty, because empty is the answer to the
        question somebody opened this panel with: a row that has not touched the books has not
        moved any money, whatever its status says, and a section that simply vanished would
        leave them thinking the panel had not loaded it.
      */}
      <Section
        title="Where the money moved"
        hint="Every movement is recorded twice, once leaving somewhere and once arriving. These two sides always add up to nothing left over."
      >
        {postings.length === 0 ? (
          <p className="muted">
            Nothing has been written to your books for this yet, so no money has actually moved.
          </p>
        ) : (
          <table className="table table--tight">
            <tbody>
              {postings.map((p, i) => (
                <Posting key={`${p.account}-${String(i)}`} posting={p} />
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {charges.length > 0 && (
        <Section
          title="What this cost you"
          hint="What outside companies charged you for this one movement. Your own fee to the customer is above."
        >
          <table className="table table--tight">
            <tbody>
              {charges.map((c, i) => (
                <Charge key={`${c.vendor}-${String(i)}`} charge={c} />
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {timeline.length > 0 && (
        <Section title="When">
          <ol className="tx-detail__timeline">
            {timeline.map((t) => (
              <li key={t.label}>
                <span>{t.label}</span>
                <span className="muted" title={t.at}>
                  {formatDateTime(t.at)}
                </span>
              </li>
            ))}
          </ol>
        </Section>
      )}

      {row.reference !== null && (
        <Section title="Reference">
          <div className="tx-detail__ref">
            <span className="mono">{row.reference}</span>
            <CopyButton value={row.reference} label="Copy reference" />
          </div>
          <p className="muted">
            Your own reference for this. Quote it to a customer; search it here to find this
            again.
          </p>
        </Section>
      )}
    </div>
  );
}

/** The customer, with the two things somebody reaches for next: their face and their number. */
function Customer({ customer }: { readonly customer: AdminTxCustomer }): JSX.Element {
  return (
    <div className="tx-detail__customer">
      <AdminAvatar userId={customer.id} name={customer.name} avatar={customer.avatar} size={44} />
      <div className="tx-detail__customer-body">
        <div className="tx-detail__customer-name">
          {customer.name}
          <Badge tone="blue">{humanize(customer.tier)}</Badge>
        </div>
        <div className="muted">
          <ContactPhone row={customer} copy compact />
          {customer.email !== null && <> · {customer.email}</>}
        </div>
      </div>
      <Link to={`/users/${customer.id}`} className="btn">
        Open customer
      </Link>
    </div>
  );
}

function Fact({ fact }: { readonly fact: AdminTxFact }): JSX.Element {
  return (
    <>
      <dt>{fact.label}</dt>
      <dd>
        <span className={fact.mono === true ? 'mono' : undefined}>{fact.value}</span>
        {/*
          Copy sits beside the values somebody retypes into somewhere else: a gateway
          dashboard, a block explorer, a bank's portal. Those are exactly the values marked
          mono, so the flag does double duty rather than needing a second one.
        */}
        {fact.mono === true && <CopyButton value={fact.value} label={`Copy ${fact.label}`} />}
        {fact.hint !== undefined && <div className="muted tx-detail__hint">{fact.hint}</div>}
      </dd>
    </>
  );
}

function Posting({ posting }: { readonly posting: AdminTxPosting }): JSX.Element {
  const out = posting.direction === 'out';
  return (
    <tr>
      <td>
        {posting.account}
        {/*
          Whose account, when it is somebody's. A panel showing "Customer wallet" twice with
          no owner is unreadable on an internal transfer, which is precisely the movement
          where knowing which wallet is the whole question.
        */}
        {posting.userId !== null && (
          <Link to={`/users/${posting.userId}`} className="faint tx-detail__owner">
            view account
          </Link>
        )}
      </td>
      <td>
        <Badge tone={out ? 'amber' : 'green'}>{out ? 'Money out' : 'Money in'}</Badge>
      </td>
      <td className="table__num mono">{formatMoney(posting.amount)}</td>
    </tr>
  );
}

function Charge({ charge }: { readonly charge: AdminTxCharge }): JSX.Element {
  return (
    <tr>
      <td>
        {charge.what}
        <div className="faint" style={{ textTransform: 'capitalize' }}>
          {charge.vendor}
        </div>
      </td>
      <td className="muted">{charge.note}</td>
      <td className="table__num mono">{formatMoney(charge.amount)}</td>
    </tr>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  readonly title: string;
  readonly hint?: string;
  readonly children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="tx-detail__section">
      <h3 className="tx-detail__section-title">{title}</h3>
      {hint !== undefined && <p className="muted tx-detail__hint">{hint}</p>}
      {children}
    </section>
  );
}
